import * as React from "react"
import { Link, useNavigate, useSearchParams } from "react-router-dom"
import KravMagaSantos from "../images/KravMagaSantos.svg"
import { resetPasswordWithToken, validateResetToken } from "../auth/api"

const inputClass =
  "w-full border-2 border-gray-200 rounded-xl text-center p-2 mt-1 bg-transparent placeholder:text-gray-400"

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const token = searchParams.get("token") || ""

  const [validating, setValidating] = React.useState(true)
  const [tokenInfo, setTokenInfo] = React.useState(null)
  const [tokenError, setTokenError] = React.useState("")
  const [novaSenha, setNovaSenha] = React.useState("")
  const [confirmarSenha, setConfirmarSenha] = React.useState("")
  const [error, setError] = React.useState("")
  const [success, setSuccess] = React.useState("")
  const [loading, setLoading] = React.useState(false)

  React.useEffect(() => {
    if (!token) {
      setTokenError("Link inválido. Solicite uma nova redefinição de senha.")
      setValidating(false)
      return
    }

    let mounted = true
    validateResetToken(token)
      .then((info) => {
        if (!mounted) return
        if (!info?.valid) {
          setTokenError(info?.message || "Link inválido ou expirado.")
          return
        }
        setTokenInfo(info)
      })
      .catch((err) => {
        if (mounted) setTokenError(err?.message || "Link inválido ou expirado.")
      })
      .finally(() => {
        if (mounted) setValidating(false)
      })

    return () => {
      mounted = false
    }
  }, [token])

  async function handleSubmit(e) {
    e.preventDefault()
    setError("")
    setSuccess("")

    if (novaSenha !== confirmarSenha) {
      setError("As senhas não coincidem.")
      return
    }
    if (novaSenha.length < 6) {
      setError("A senha deve ter pelo menos 6 caracteres.")
      return
    }

    try {
      setLoading(true)
      const data = await resetPasswordWithToken({ token, novaSenha })
      setSuccess(data?.message || "Senha redefinida com sucesso!")
      setTimeout(() => navigate("/login", { replace: true }), 2000)
    } catch (err) {
      setError(err?.message || "Não foi possível redefinir a senha.")
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen w-full bg-[#00072D] lg:bg-gray-50 font-oswald flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="flex justify-center mb-4">
          <img
            src={KravMagaSantos}
            alt="Krav Maga Logo"
            className="h-24 object-contain drop-shadow-lg invert lg:invert-0"
          />
        </div>

        <div className="bg-slate-50 lg:bg-[#abb2b9] rounded-3xl py-6 px-8 drop-shadow-xl">
          <h1 className="text-3xl font-semibold text-cor-base text-center">
            Redefinir senha
          </h1>

          {validating ? (
            <p className="text-center text-cor-base mt-6">Validando link...</p>
          ) : tokenError ? (
            <div className="mt-6 text-center">
              <p className="text-red-700 font-semibold">{tokenError}</p>
              <Link
                to="/login"
                className="inline-block mt-4 text-cor-base font-semibold hover:underline"
              >
                Voltar ao login
              </Link>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="mt-6 grid gap-4 text-cor-base">
              <p className="text-sm text-center">
                Conta: <strong>{tokenInfo?.nomeCompleto}</strong>
                <br />
                <span className="text-slate-600">{tokenInfo?.email}</span>
              </p>

              <div>
                <label className="text-lg font-medium">Nova senha</label>
                <input
                  type="password"
                  className={inputClass}
                  value={novaSenha}
                  onChange={(e) => setNovaSenha(e.target.value)}
                  required
                  autoComplete="new-password"
                />
              </div>

              <div>
                <label className="text-lg font-medium">Confirmar nova senha</label>
                <input
                  type="password"
                  className={inputClass}
                  value={confirmarSenha}
                  onChange={(e) => setConfirmarSenha(e.target.value)}
                  required
                  autoComplete="new-password"
                />
              </div>

              {error && (
                <p className="text-red-700 text-center font-semibold">{error}</p>
              )}
              {success && (
                <p className="text-green-700 text-center font-semibold">{success}</p>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full py-3 text-xl bg-cor-base text-white rounded-xl hover:scale-[1.01] transition-all disabled:opacity-60"
              >
                {loading ? "Salvando..." : "Salvar nova senha"}
              </button>

              <p className="text-center">
                <Link to="/login" className="text-sm font-semibold hover:underline">
                  Voltar ao login
                </Link>
              </p>
            </form>
          )}
        </div>
      </div>
    </div>
  )
}
